(() => {
  const NS = '[BedConfigurator]';

  function normalize(value) {
    return String(value || '').trim();
  }

  function normalizeKey(value) {
    return normalize(value).toLowerCase();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function sizeKey(size) {
    const key = normalizeKey(size);

    if (key === 'small' || key === 's') return 'small';
    if (key === 'medium' || key === 'm') return 'medium';
    if (key === 'large' || key === 'l') return 'large';

    return '';
  }

  function sizeBadge(size) {
    const key = normalizeKey(size);

    if (key === 'small' || key === 's') return 'S';
    if (key === 'medium' || key === 'm') return 'M';
    if (key === 'large' || key === 'l') return 'L';

    return normalize(size);
  }

  function sizeOrder(size) {
    const key = normalizeKey(size);

    if (key === 'small' || key === 's') return 1;
    if (key === 'medium' || key === 'm') return 2;
    if (key === 'large' || key === 'l') return 3;

    return 99;
  }

  function colorSwatch(color) {
    const key = normalizeKey(color);

    const map = {
        black: '#000000',
        grey: '#8E8E93',
        gray: '#8E8E93',
        'dark grey': '#5A5A5E',
        'dark gray': '#5A5A5E',
        white: '#FFFFFF',
        cream: '#D8C3A5',
        brown: '#8D6E63',
        'cow brown': 'conic-gradient(from 90deg, #763D30 0% 50%, #DFDDCC 50% 100%)',
        wood: '#C8A165',
        wooden: '#C8A165',
        green: '#34C759',
    };

    return map[key] || '#d0cfd2';
  }

  function getMatchingVariant(product, state) {
    if (!Array.isArray(product.variants)) return null;

    return product.variants.find((variant) => {
      const options = variant.options || [];

      const sizeMatch =
        !state.size ||
        options.some((option) => normalizeKey(option) === normalizeKey(state.size));

      const colorMatch =
        !state.color ||
        options.some((option) => normalizeKey(option) === normalizeKey(state.color));

      return sizeMatch && colorMatch;
    }) || product.variants[0] || null;
  }

  function buildCard(product, state) {
    const variant = getMatchingVariant(product, state);
    const url = variant?.url || product.url;
    const image = variant?.image || product.image;
    const price = variant?.price || '';

    return `
      <article class="bed-configurator-card">
        <a href="${escapeHtml(url)}" class="bed-configurator-card__media">
          ${
            image
              ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
              : `<div class="bed-configurator-card__placeholder"></div>`
          }
        </a>

        <div class="bed-configurator-card__body">
          <a href="${escapeHtml(url)}" class="bed-configurator-card__title">
            ${escapeHtml(product.title)}
          </a>

          <div class="bed-configurator-card__meta">
            ${state.size ? `${escapeHtml(state.size)}` : ''}
            ${state.color ? ` / ${escapeHtml(state.color)}` : ''}
          </div>

          ${price ? `<div class="bed-configurator-card__price">${escapeHtml(price)}</div>` : ''}

          <div class="bed-configurator-card__cta">
            <a href="${escapeHtml(url)}" class="bed-configurator-card__btn">
              View product
            </a>
          </div>
        </div>
      </article>
    `;
  }

  function unique(values) {
    return [...new Set(values.map(normalize).filter(Boolean))];
  }

  function renderOptions(host, values, type, selected) {
    if (!host) return;

    host.innerHTML = values
      .map((value) => {
        const active = normalizeKey(value) === normalizeKey(selected);

        if (type === 'size') {
          return `
            <button
              type="button"
              class="bed-configurator__option bed-configurator__option--size ${active ? 'is-active' : ''}"
              data-filter="${type}"
              data-value="${escapeHtml(value)}"
              aria-label="${escapeHtml(value)}"
            >
              <span class="bed-configurator__size-badge">${escapeHtml(sizeBadge(value))}</span>
              <span>${escapeHtml(value)}</span>
            </button>
          `;
        }

        return `
          <button
            type="button"
            class="bed-configurator__option bed-configurator__option--color ${active ? 'is-active' : ''}"
            data-filter="${type}"
            data-value="${escapeHtml(value)}"
            aria-label="${escapeHtml(value)}"
          >
            <span
              class="bed-configurator__color-swatch"
              style="--swatch:${escapeHtml(colorSwatch(value))}"
            ></span>
            <span>${escapeHtml(value)}</span>
          </button>
        `;
      })
      .join('');
  }

  async function loadBreeds(section) {
    const url = section.dataset.breedsUrl;
    if (!url) return {};

    try {
      const res = await fetch(url, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      console.warn(NS, 'Could not load breeds JSON', err);
      return {};
    }
  }

  function renderBreeds(section, breeds, selectedSize) {
    const host = section.querySelector('[data-breed-list]');
    if (!host) return;

    const key = sizeKey(selectedSize);
    const list = key && Array.isArray(breeds[key]) ? breeds[key] : [];

    if (!list.length) {
      host.innerHTML = `<li>Select a size to see typical breeds.</li>`;
      return;
    }

    host.innerHTML = list.map((breed) => `<li>${escapeHtml(breed)}</li>`).join('');
  }

  function init(section) {
    const jsonEl = section.querySelector('[data-bed-products]');
    if (!jsonEl) return;

    let products = [];

    try {
      products = JSON.parse(jsonEl.textContent || '[]').filter((product) => product && product.title);
    } catch (err) {
      console.error(NS, 'Invalid embedded product JSON', err);
      return;
    }

    const state = {
      size: '',
      color: '',
    };

    const sizeHost = section.querySelector('[data-filter-group="size"]');
    const colorHost = section.querySelector('[data-filter-group="color"]');
    const countHost = section.querySelector('[data-results-count]');
    const resultsHost = section.querySelector('[data-results-grid]');
    const prevBtn = section.querySelector('[data-slider-prev]');
    const nextBtn = section.querySelector('[data-slider-next]');

    let breeds = {};

    function getFilteredProducts() {
      return products.filter((product) => {
        const sizeMatch =
          !state.size ||
          (product.sizes || []).some((size) => normalizeKey(size) === normalizeKey(state.size));

        const colorMatch =
          !state.color ||
          (product.colors || []).some((color) => normalizeKey(color) === normalizeKey(state.color));

        return sizeMatch && colorMatch;
      });
    }

    function bindFilterClicks() {
      section.querySelectorAll('[data-filter]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const type = btn.dataset.filter;
          const value = btn.dataset.value;

          state[type] =
            normalizeKey(state[type]) === normalizeKey(value)
              ? ''
              : value;

          render();
        });
      });
    }

    function render() {
      const sizes = unique(
        products
          .flatMap((product) => product.sizes || [])
          .filter((size) => {
            const key = normalizeKey(size);
            return key === 'small' || key === 's' || key === 'medium' || key === 'm' || key === 'large' || key === 'l';
          })
      ).sort((a, b) => sizeOrder(a) - sizeOrder(b));

      const colors = unique(products.flatMap((product) => product.colors || []));

      renderOptions(sizeHost, sizes, 'size', state.size);
      renderOptions(colorHost, colors, 'color', state.color);

      const filteredProducts = getFilteredProducts().sort((a, b) =>
        String(a.title || '').localeCompare(String(b.title || ''), undefined, {
          sensitivity: 'base',
        })
      );

      if (countHost) {
        countHost.textContent = `${filteredProducts.length} matching product${filteredProducts.length === 1 ? '' : 's'}`;
      }

      if (resultsHost) {
        resultsHost.innerHTML = filteredProducts.length
          ? filteredProducts.map((product) => buildCard(product, state)).join('')
          : `<div class="bed-configurator__empty">No products match this combination.</div>`;

        resultsHost.scrollTo({ left: 0, behavior: 'smooth' });
      }

      renderBreeds(section, breeds, state.size);
      bindFilterClicks();
    }

    prevBtn?.addEventListener('click', () => {
      resultsHost?.scrollBy({ left: -280, behavior: 'smooth' });
    });

    nextBtn?.addEventListener('click', () => {
      resultsHost?.scrollBy({ left: 280, behavior: 'smooth' });
    });

    loadBreeds(section).then((loadedBreeds) => {
      breeds = loadedBreeds || {};
      render();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('s-bed-configurator').forEach(init);
  });
})();
